import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Edit3 } from 'lucide-react';

import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { adminApi } from '../../lib/adminApi';

/**
 * Iter 41.7 — list of every client-specific fork of a questionnaire.
 *
 * Renders nothing while loading or when there are zero forks. When ≥1 fork
 * exists it shows a pill ("X custom versions") that opens a popover with
 * the client name + a small "Edit" button that calls `onEdit(version)`.
 *
 * Props:
 *   questionnaireId  the canonical questionnaire id
 *   visible          whether the current user can see this (admin/advisor)
 *   onEdit(version)  callback when the consultant clicks "Edit" for one fork
 */
export function CustomVersionsBadge({ questionnaireId, visible, onEdit }) {
  const { t } = useTranslation();
  const [versions, setVersions] = useState(null); // null = loading, [] = none
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!visible || !questionnaireId) return;
    let live = true;
    (async () => {
      try {
        const data = await adminApi.listClientVersions(questionnaireId);
        if (live) setVersions(data?.versions || []);
      } catch {
        if (live) setVersions([]);
      }
    })();
    return () => { live = false; };
  }, [questionnaireId, visible, reloadToken]);

  if (!visible || versions === null || versions.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary hover:bg-primary/15 transition-colors"
          data-testid="qnr-custom-versions-badge"
          onClick={() => setReloadToken((x) => x + 1)}
        >
          <Edit3 className="h-3 w-3" />
          {t('common.qcustom.customVersionsBadge', { count: versions.length })}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-3"
        data-testid="qnr-custom-versions-popover"
      >
        <div className="text-xs font-semibold mb-2">
          {t('common.qcustom.customVersionsTitle')}
        </div>
        <ul className="space-y-1.5">
          {versions.map((v) => (
            <li
              key={v.id}
              className="flex items-center justify-between gap-2"
              data-testid={`qnr-custom-version-${v.client_id}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm truncate">{v.client_name}</span>
                <Badge variant="outline" className="text-xs shrink-0">v{v.version_number}</Badge>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 gap-1 shrink-0"
                onClick={() => onEdit?.(v)}
                data-testid={`qnr-edit-version-${v.client_id}`}
              >
                <Edit3 className="h-3 w-3" />
                {t('common.qcustom.editForClient')}
              </Button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export default CustomVersionsBadge;
